/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import React from 'react';
import PropTypes from 'prop-types/prop-types';
import { Button, Flex, Link, Title } from '@gjensidige/builders-components';
import type { ProductButtonsProps as Props } from './types';

import styles from '../less/product-buttons.module.less';

export const ProductButtons = ({
  title,
  buttons,
  compact = false,
  showIcons = true,
  links
}: Props) =>
  buttons.length ? (
    <Flex
      align="start"
      as="section"
      center
      className={styles.marginYParts}
      gap="sm xl"
      layout={{ xs: 1, md: compact ? 1 : 2 }}
    >
      <Title size={compact ? 5 : 3} uppercase={compact} text={title} />
      <Flex layout="1" gap="sm">
        <Flex
          className={styles.buttons}
          gap={{ xs: 'sm xs', md: 'sm' }}
          wrap={compact ? 'nowrap' : 'wrap'}
        >
          {buttons?.map(({ icon, target, trackingLabel, text }, index) => (
            <Link
              className={styles.button}
              href={target}
              key={index}
              tracking={{
                eventAction: 'click',
                eventCategory: 'link',
                eventKey: 'button',
                eventLabel: trackingLabel ?? `product-buttons-module_${target}`,
                eventModule: 'product-buttons-module',
                eventSource: '@gjensidige/parts-product-buttons',
                eventVersion: '1.0.0'
              }}
            >
              {!!icon && showIcons && <img src={icon} alt="" loading="lazy" />}
              {text}
            </Link>
          ))}
        </Flex>
        {!!links?.length && (
          <Flex gap={{ xs: 'sm md', md: 'sm xl' }}>
            {links?.filter(Boolean).map((link, index) => (
              <Button
                href={link?.url}
                key={index}
                variant="action"
                tracking={{
                  eventLabel:
                    link?.trackingLabel || `product-buttons-module_${link?.url}`
                }}
              >
                {link?.text}
              </Button>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  ) : null;

ProductButtons.CSR = true;
ProductButtons.propTypes = {
  title: PropTypes.string.isRequired,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      target: PropTypes.string
    })
  ).isRequired,
  compact: PropTypes.bool,
  showIcons: PropTypes.bool,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
      newWindow: PropTypes.bool
    })
  )
};

export default ProductButtons;
